import type { TranslationKey } from './index';

/**
 * Bengali (বাংলা). Typed as `Record<TranslationKey, string>` on purpose: adding a
 * key to `en.ts` without one here is a compile error, which is the only reliable
 * way to keep a second language from quietly rotting.
 *
 * Platform names (Facebook, Instagram, …) stay in Latin script because that is
 * how they are written and searched for in Bengali usage — transliterating them
 * would make them harder to recognise, not easier.
 *
 * Note this dictionary controls the *interface* only. What language a post is
 * generated in comes from the brand profile, so switching to Bengali here never
 * changes what goes out to an audience.
 */
export const bn: Record<TranslationKey, string> = {
  // ─── Shared ────────────────────────────────────────────────────────────────
  'common.appName': 'সোশ্যাল কনটেন্ট স্টুডিও',
  'common.appTagline': 'সব চ্যানেলে কনটেন্ট পরিকল্পনা, তৈরি ও প্রকাশ',
  'common.brand': 'সোশ্যাল স্টুডিও',
  'common.back': 'ফিরে যান',
  'common.dashboard': 'ড্যাশবোর্ড',
  'common.previous': 'পূর্ববর্তী',
  'common.continue': 'পরবর্তী',
  'common.loading': 'লোড হচ্ছে…',
  'common.processing': 'প্রক্রিয়াধীন…',
  'common.cancel': 'বাতিল',
  'common.confirm': 'নিশ্চিত করুন',
  'common.edit': 'সম্পাদনা',
  'common.done': 'সম্পন্ন',
  'common.view': 'দেখুন',
  'common.prev': 'পূর্ব',
  'common.next': 'পরবর্তী',
  'common.signIn': 'সাইন ইন',
  'common.signOut': 'সাইন আউট',
  'common.expired': 'মেয়াদোত্তীর্ণ',
  'common.emptyValue': '—',
  'common.dismiss': 'বন্ধ করুন',

  // ─── Workspaces (tenants) ──────────────────────────────────────────────────
  'workspace.label': 'ওয়ার্কস্পেস',
  'workspace.members': '{count} জন সদস্য',
  'workspace.roleAdmin': 'অ্যাডমিন',
  'workspace.roleMember': 'সদস্য',
  'workspace.create': 'নতুন ওয়ার্কস্পেস',
  'common.save': 'সংরক্ষণ',
  'workspace.none': 'কোনো ওয়ার্কস্পেস নেই',
  'team.noWorkspace': 'আপনি এখনো কোনো ওয়ার্কস্পেসে নেই।',
  'team.actionFailed': 'কাজটি সম্পন্ন হয়নি। আবার চেষ্টা করুন।',
  'team.invite.title': 'কাউকে আমন্ত্রণ জানান',
  'team.invite.emailPlaceholder': 'name@company.com',
  'team.invite.submit': 'আমন্ত্রণ',
  'team.invite.adminCount': '{max}টির মধ্যে {count}টি অ্যাডমিন আসন ব্যবহৃত',
  'team.members.title': 'সদস্যরা',
  'team.members.overrides': '{count}টি কাস্টম',
  'team.members.remove': 'ওয়ার্কস্পেস থেকে সরান',
  'team.invitations.title': 'অপেক্ষমাণ আমন্ত্রণ',
  'team.invitations.expires': '{date} তারিখে মেয়াদ শেষ',
  'team.invitations.revoke': 'প্রত্যাহার',
  'team.teams.title': 'টিম',
  'team.teams.namePlaceholder': 'টিমের নাম',
  'team.teams.create': 'তৈরি করুন',
  'team.teams.delete': 'টিম মুছুন',
  'team.teams.empty': 'এখনো কোনো টিম নেই।',
  'team.permissions.edit': 'অনুমতি সম্পাদনা',
  'team.permissions.help': 'ইনহেরিট ভূমিকা অনুসরণ করে। গ্র্যান্ট ও রিভোক তা অগ্রাহ্য করে এবং ভূমিকা পরিবর্তনেও টিকে থাকে।',
  'team.permissions.byRole': 'এই ভূমিকায় অন্তর্ভুক্ত',
  'team.permissions.notByRole': 'এই ভূমিকায় নেই',
  'team.permissions.inherit': 'ইনহেরিট',
  'team.permissions.grant': 'গ্র্যান্ট',
  'team.permissions.revoke': 'রিভোক',
  'team.permissions.saveFailed': 'অনুমতিগুলো সংরক্ষণ করা যায়নি।',

  // ─── Language toggle ───────────────────────────────────────────────────────
  'language.switchTo': '{language} এ পরিবর্তন করুন',

  // ─── Payment / subscription statuses ───────────────────────────────────────
  'status.pending': 'অপেক্ষমাণ',
  'status.completed': 'সম্পন্ন',
  'status.failed': 'ব্যর্থ',
  'status.cancelled': 'বাতিল',
  'status.active': 'সক্রিয়',
  'status.expired': 'মেয়াদোত্তীর্ণ',

  // ─── Platforms ─────────────────────────────────────────────────────────────
  'platform.facebook': 'Facebook',
  'platform.instagram': 'Instagram',

  // ─── Industry verticals ────────────────────────────────────────────────────
  'vertical.restaurant': 'রেস্তোরাঁ ও ক্যাফে',
  'vertical.dental': 'ডেন্টাল ক্লিনিক',
  'vertical.real_estate': 'রিয়েল এস্টেট',
  'vertical.cleaning': 'ক্লিনিং কোম্পানি',
  'vertical.agency': 'এজেন্সি',
  'vertical.other': 'অন্যান্য',

  // ─── Post lifecycle ────────────────────────────────────────────────────────
  'postStatus.draft': 'খসড়া',
  'postStatus.pending_review': 'পর্যালোচনার অপেক্ষায়',
  'postStatus.approved': 'অনুমোদিত',
  'postStatus.scheduled': 'নির্ধারিত',
  'postStatus.publishing': 'প্রকাশ হচ্ছে…',
  'postStatus.published': 'প্রকাশিত',
  'postStatus.failed': 'ব্যর্থ',
  'postStatus.cancelled': 'বাতিল',

  // ─── Login ─────────────────────────────────────────────────────────────────
  'login.title': 'সোশ্যাল কনটেন্ট স্টুডিও',
  'login.subtitle': 'AI কনটেন্ট — নির্ধারিত সময়ে স্বয়ংক্রিয়ভাবে প্রকাশিত',
  'login.welcome': 'স্বাগতম',
  'login.description': 'আপনার কনটেন্ট ও সংযুক্ত অ্যাকাউন্ট পরিচালনা করতে সাইন ইন করুন।',
  'login.google': 'Google দিয়ে চালিয়ে যান',
  'login.terms':
    'সাইন ইন করার মাধ্যমে আপনি আমাদের সেবার শর্তাবলীতে সম্মত হচ্ছেন। নতুন ব্যবহারকারীরা ১৪ দিনের ফ্রি ট্রায়াল পান।',
  'login.suspendedTitle': 'এই অ্যাকাউন্টটি স্থগিত',
  'login.suspendedBody':
    'স্থগিতাদেশ বলবৎ থাকা অবস্থায় সাইন ইন বন্ধ থাকবে। ভুল হয়েছে মনে করলে সাপোর্টে যোগাযোগ করুন।',
  'login.failedTitle': 'সাইন ইন ব্যর্থ হয়েছে',

  // ─── Session expiry ────────────────────────────────────────────────────────
  'session.title': 'আপনার সেশনের মেয়াদ শেষ',
  'session.description':
    'নিরাপত্তার জন্য আপনাকে সাইন আউট করা হয়েছে। চালিয়ে যেতে আবার লগ ইন করুন — সংরক্ষণ না করা পরিবর্তনগুলো আবার করতে হবে।',
  'session.logout': 'লগ আউট',

  // ─── Account suspension ────────────────────────────────────────────────────
  'suspended.title': 'আপনার অ্যাকাউন্ট স্থগিত',
  'suspended.description':
    'একজন প্রশাসক এই অ্যাকাউন্টটি স্থগিত করেছেন, তাই স্থগিতাদেশ শেষ না হওয়া পর্যন্ত এটি ব্যবহার করা যাবে না। পুনরায় সাইন ইন করলেও অ্যাক্সেস ফিরে আসবে না।',
  'suspended.descriptionIndefinite':
    'একজন প্রশাসক এই অ্যাকাউন্টটি অনির্দিষ্টকালের জন্য স্থগিত করেছেন। পুনরায় সাইন ইন করলেও অ্যাক্সেস ফিরে আসবে না — ভুল হয়েছে মনে করলে সাপোর্টে যোগাযোগ করুন।',
  'suspended.reasonLabel': 'কারণ',
  'suspended.untilLabel': 'স্থগিত থাকবে',
  'suspended.indefinite': 'অনির্দিষ্টকাল',
  'suspended.logout': 'লগ আউট',

  // ─── Dashboard navigation ──────────────────────────────────────────────────
  'nav.content': 'কনটেন্ট',
  'nav.setup': 'সেটআপ',
  'nav.analytics': 'বিশ্লেষণ',
  'nav.account': 'অ্যাকাউন্ট',
  'nav.calendar': 'ক্যালেন্ডার',
  'nav.composer': 'কম্পোজার',
  'nav.approvals': 'অনুমোদন',
  'nav.library': 'মিডিয়া লাইব্রেরি',
  'nav.brands': 'ব্র্যান্ড',
  'nav.accounts': 'সংযুক্ত অ্যাকাউন্ট',
  'nav.subscription': 'সাবস্ক্রিপশন',
  'nav.payments': 'পেমেন্ট ইতিহাস',
  'nav.admin': 'প্রশাসন',
  'nav.adminUsers': 'ব্যবহারকারী',
  'nav.adminActivity': 'ব্যবহারকারীর কার্যক্রম',
  'nav.adminStats': 'পরিসংখ্যান',
  'nav.posts': 'পোস্ট',
  'nav.drafts': 'ড্রাফট',
  'nav.inbox': 'ইনবক্স',
  'nav.team': 'টিম',
  'nav.settings': 'সেটিংস',
  'nav.createPost': 'পোস্ট তৈরি করুন',
  'nav.notifications': 'নোটিফিকেশন',
  'nav.expand': 'প্যানেল প্রসারিত করুন',
  'nav.collapse': 'প্যানেল সংকুচিত করুন',
  'nav.resize': 'প্যানেলের আকার পরিবর্তন — অ্যারো কী ব্যবহার করুন',
  'nav.dragResize': 'টেনে আকার পরিবর্তন করুন',
  'nav.breadcrumb': 'হোম / ড্যাশবোর্ড',

  // ─── Not-yet-built tabs ────────────────────────────────────────────────────
  'placeholder.postsTitle': 'পোস্ট তালিকা শীঘ্রই আসছে',
  'placeholder.postsBody':
    'প্রকাশিত ও নির্ধারিত পোস্টগুলো এখানে দেখা যাবে। কম্পোজার খুলতে “পোস্ট তৈরি করুন” ব্যবহার করুন।',
  'placeholder.draftsTitle': 'ড্রাফট শীঘ্রই আসছে',
  'placeholder.draftsBody':
    'পোস্ট সার্ভারে সংরক্ষিত হওয়া শুরু হলে কম্পোজার থেকে সংরক্ষিত ড্রাফটগুলো এখানে দেখা যাবে।',
  'placeholder.inboxTitle': 'ইনবক্স শীঘ্রই আসছে',
  'placeholder.inboxBody':
    'প্ল্যাটফর্ম API যুক্ত হলে সব সংযুক্ত অ্যাকাউন্টের মন্তব্য ও মেনশন এখানে আসবে।',
  'placeholder.teamTitle': 'টিম ব্যবস্থাপনা শীঘ্রই আসছে',
  'placeholder.teamBody':
    'মাল্টি-টেন্যান্ট সদস্যপদ তৈরি হলে সহকর্মীদের ওয়ার্কস্পেসে আমন্ত্রণ ও ভূমিকা নির্ধারণ করা যাবে।',
  'placeholder.settingsTitle': 'সেটিংস শীঘ্রই আসছে',
  'placeholder.settingsBody': 'ওয়ার্কস্পেস পছন্দ, ব্র্যান্ডিং ও নোটিফিকেশন নিয়ম এখানে থাকবে।',
  'placeholder.calendarTitle': 'ক্যালেন্ডার শীঘ্রই আসছে',
  'placeholder.calendarBody':
    'প্রকাশনা পাইপলাইনের সাথে সময় নির্ধারণ যুক্ত হবে। আগে আসবে সংযুক্ত অ্যাকাউন্ট ও কনটেন্ট তৈরি।',

  // ─── Connected accounts ─────────────────────────────────────────────────────
  'accounts.title': 'সংযুক্ত অ্যাকাউন্ট',
  'accounts.subtitle': 'কম্পোজার থেকে পোস্ট প্রকাশ করতে আপনার সোশ্যাল অ্যাকাউন্ট সংযুক্ত করুন।',
  'accounts.connected': 'সংযুক্ত',
  'accounts.notConnected': 'সংযুক্ত নয়',
  'accounts.activeLabel': 'সক্রিয় এবং প্রকাশের জন্য প্রস্তুত',
  'accounts.inactiveLabel': 'এখনো সংযুক্ত হয়নি',
  'accounts.connect': 'সংযুক্ত করুন',
  'accounts.disconnect': 'সংযোগ বিচ্ছিন্ন করুন',
  'accounts.desc.facebook': 'আপনার Facebook পেজে পোস্ট, ক্যারোসেল ও ভিডিও প্রকাশ করুন।',
  'accounts.desc.instagram': 'Instagram-এ ছবি, রিলস, স্টোরি ও ক্যারোসেল শেয়ার করুন।',
  'accounts.noBrand': 'প্রথমে একটি ব্র্যান্ড তৈরি করুন — সংযুক্ত অ্যাকাউন্ট একটি ব্র্যান্ডের অধীনে থাকে।',
  'accounts.error': 'সংযোগটি সম্পন্ন হয়নি। আবার চেষ্টা করুন।',
  'accounts.connectAnother': 'আরেকটি সংযুক্ত করুন',
  'accounts.needsReauth': 'পুনঃসংযোগ প্রয়োজন',
  'accounts.picker.title': 'কী সংযুক্ত করবেন বেছে নিন',
  'accounts.picker.subtitle': 'এই লগইন যেসব অ্যাকাউন্টে পোস্ট করতে পারে।',
  'accounts.picker.confirm': '{count}টি সংযুক্ত করুন',
  'accounts.picker.alreadyConnected': 'সংযুক্ত',

  'placeholder.composerTitle': 'কম্পোজার শীঘ্রই আসছে',
  'placeholder.composerBody':
    'ব্র্যান্ড প্রোফাইল তৈরি হওয়ার পর পরবর্তী ধাপে AI জেনারেশন যুক্ত হবে।',
  'placeholder.brandsTitle': 'ব্র্যান্ড সেটআপ শীঘ্রই আসছে',
  'placeholder.brandsBody':
    'এটিই প্রথমে তৈরি হচ্ছে — যে ব্যবসায়িক প্রোফাইল থেকে প্রতিটি পোস্ট লেখা হবে।',
  'placeholder.accountsTitle': 'অ্যাকাউন্ট সংযোগ শীঘ্রই আসছে',
  'placeholder.accountsBody':
    'Facebook, Instagram, LinkedIn, TikTok ও YouTube সংযোগ প্ল্যাটফর্ম পর্যালোচনার উপর নির্ভরশীল, যা ইতিমধ্যে চলছে।',
  'placeholder.libraryTitle': 'মিডিয়া লাইব্রেরি শীঘ্রই আসছে',
  'placeholder.libraryBody': 'মিডিয়া পাইপলাইনের সাথে ছবি ও ভিডিও আপলোড যুক্ত হবে।',
  'placeholder.approvalsTitle': 'অনুমোদন শীঘ্রই আসছে',
  'placeholder.approvalsBody':
    'সময় নির্ধারণের সাথে প্রকাশের আগে পর্যালোচনা যুক্ত হবে। নিয়ন্ত্রিত খাতের জন্য এটি ডিফল্টে চালু থাকবে।',
  'placeholder.analyticsTitle': 'বিশ্লেষণ শীঘ্রই আসছে',
  'placeholder.analyticsBody':
    'পোস্ট প্রকাশ শুরু হলে ও পরিমাপযোগ্য তথ্য জমা হলে প্রতি পোস্টের ফলাফল দেখা যাবে।',

  // ─── Dashboard: subscription tab ───────────────────────────────────────────
  'subscription.signInPrompt': 'সাবস্ক্রিপশন পরিচালনা করতে সাইন ইন করুন।',
  'subscription.loadError': 'সাবস্ক্রিপশন লোড করা যায়নি।',
  'subscription.planLabel': '{plan} প্ল্যান',
  'subscription.daysLeft': '{days} দিন বাকি',
  'subscription.daysShort': '{days}দি',
  'subscription.renewsInDays': '{days} দিনে নবায়ন',
  'subscription.upgrade': 'আপগ্রেড',
  'subscription.days': '{days} দিন',
  'subscription.postsUsed': 'প্রকাশিত পোস্ট',
  'subscription.generationsUsed': 'AI জেনারেশন',
  'subscription.storageUsed': 'মিডিয়া স্টোরেজ',
  'subscription.expiryWarning': '{days} দিনে মেয়াদ শেষ। শীঘ্রই নবায়ন করুন।',
  'subscription.expiryWarningOne': '১ দিনে মেয়াদ শেষ। শীঘ্রই নবায়ন করুন।',
  'subscription.viewUpgrade': 'প্ল্যান দেখুন / আপগ্রেড করুন',
  'subscription.renewalTitle': 'আপনার সাবস্ক্রিপশনের মেয়াদ শেষ',
  'subscription.renewalBody': 'প্রকাশনা চালিয়ে যেতে আবার সাবস্ক্রাইব করুন।',
  'subscription.renewalAction': 'প্ল্যান দেখুন',
  'subscription.renewalDismiss': 'পরে মনে করিয়ে দিন',
  'subscription.cancelAction': 'সাবস্ক্রিপশন বাতিল করুন',
  'subscription.cancelling': 'বাতিল হচ্ছে…',
  'subscription.cancelFailed': 'বাতিল করা যায়নি। আবার চেষ্টা করুন।',
  'subscription.confirmTitle': 'সাবস্ক্রিপশন বাতিল করবেন?',
  'subscription.confirmBody':
    'বর্তমান মেয়াদ শেষ না হওয়া পর্যন্ত অ্যাক্সেস থাকবে। ওই তারিখের পরে নির্ধারিত পোস্টগুলো প্রকাশ হবে না।',
  'subscription.confirmYes': 'সাবস্ক্রিপশন বাতিল করুন',
  'subscription.confirmNo': 'রেখে দিন',
  'subscription.limitsTitle': 'আপনার প্ল্যানের সীমা',
  'subscription.limitBrands': 'ব্র্যান্ড:',
  'subscription.limitAccounts': 'সংযুক্ত অ্যাকাউন্ট:',
  'subscription.limitHorizon': 'আগাম নির্ধারণ:',
  'subscription.postsRemaining': 'এ মাসে বাকি পোস্ট:',

  // ─── Dashboard: payments tab ───────────────────────────────────────────────
  'payments.signInPrompt': 'আপনার পেমেন্ট দেখতে সাইন ইন করুন।',
  'payments.emptyTitle': 'এখনো কোনো পেমেন্ট নেই',
  'payments.emptyBody': 'আপগ্রেড করলে আপনার কেনাকাটা এখানে দেখা যাবে।',
  'payments.viewPlans': 'প্ল্যান দেখুন',

  // ─── Pricing ───────────────────────────────────────────────────────────────
  'pricing.badge': 'সাবস্ক্রিপশন প্ল্যান',
  'pricing.title': 'আপনার প্ল্যান বেছে নিন',
  'pricing.subtitle': 'AI-লিখিত কনটেন্ট — আপনার সংযুক্ত প্রতিটি চ্যানেলে নির্ধারিত সময়ে প্রকাশিত।',
  'pricing.perMonth': 'প্রতি মাসে',
  'pricing.days14': '১৪ দিন',
  'pricing.contactUs': 'যোগাযোগ করুন',
  'pricing.custom': 'কাস্টম',
  'pricing.badgeFree': '১৪ দিন ফ্রি',
  'pricing.badgeBest': 'সেরা মূল্য',
  'pricing.planFreeTrial': 'ফ্রি ট্রায়াল',
  'pricing.planStarter': 'স্টার্টার',
  'pricing.planGrowth': 'গ্রোথ',
  'pricing.planAgency': 'এজেন্সি',
  'pricing.planEnterprise': 'এন্টারপ্রাইজ',
  'pricing.planLegacyProTrial': 'প্রো ট্রায়াল',
  'pricing.planLegacyPro': 'প্রো',
  'pricing.ctaFreeTrial': 'ফ্রি ট্রায়াল শুরু করুন',
  'pricing.ctaStarter': 'স্টার্টার নিন',
  'pricing.ctaGrowth': 'গ্রোথ নিন',
  'pricing.ctaAgency': 'এজেন্সি নিন',
  'pricing.ctaEnterprise': 'সেলসে যোগাযোগ করুন',
  'pricing.featBrands': '{count} টি ব্র্যান্ড',
  'pricing.featBrandsPlural': '{count} টি ব্র্যান্ড',
  'pricing.featAccounts': '{count} টি সংযুক্ত অ্যাকাউন্ট',
  'pricing.featPosts': 'মাসে {count} টি পোস্ট',
  'pricing.featGenerations': 'মাসে {count} টি AI জেনারেশন',
  'pricing.featStorage': '{amount} মিডিয়া স্টোরেজ',
  'pricing.featPlatforms': '{platforms} এ প্রকাশ',
  'pricing.featScheduleHorizon': '{days} দিন আগাম নির্ধারণ',
  'pricing.featApprovals': 'অনুমোদন প্রক্রিয়া',
  'pricing.featAnalytics': 'পোস্ট বিশ্লেষণ',
  'pricing.featWorkspaces': 'ক্লায়েন্ট ওয়ার্কস্পেস',
  'pricing.featSupport': 'ডেডিকেটেড সাপোর্ট',
  'pricing.featCustomVolume': 'কাস্টম পরিমাণ',
  'pricing.featMultiUser': 'একাধিক ব্যবহারকারী',
  'pricing.featBranding': 'কাস্টম ব্র্যান্ডিং',
  'pricing.featInvoice': 'ইনভয়েস বিলিং',
  'pricing.payFailed': 'পেমেন্ট ব্যর্থ হয়েছে। আবার চেষ্টা করুন।',

  // ─── Payment result ────────────────────────────────────────────────────────
  'payment.successTitle': 'পেমেন্ট সফল!',
  'payment.successBody': 'আপনার সাবস্ক্রিপশন এখন সক্রিয়। এখনই কনটেন্ট পরিকল্পনা শুরু করুন।',
  'payment.plan': 'প্ল্যান',
  'payment.postsPerMonth': 'পোস্ট/মাস',
  'payment.expires': 'মেয়াদ শেষ',
  'payment.activating': 'সাবস্ক্রিপশন সক্রিয় হচ্ছে…',
  'payment.tranId': 'ট্রানজেকশন আইডি: {id}',
  'payment.goToDashboard': 'ড্যাশবোর্ডে যান',
  'payment.receipt': 'আপনার নিবন্ধিত ইমেইলে একটি রসিদ পাঠানো হয়েছে।',
  'payment.failTitle': 'পেমেন্ট ব্যর্থ',
  'payment.failBody': 'আপনার পেমেন্ট প্রক্রিয়া করা যায়নি। কোনো চার্জ কাটা হয়নি।',
  'payment.tryAgain': 'আবার চেষ্টা করুন',
  'payment.backToDashboard': 'ড্যাশবোর্ডে ফিরুন',
  'payment.cancelTitle': 'পেমেন্ট বাতিল',
  'payment.cancelBody': 'আপনি পেমেন্ট বাতিল করেছেন। আপনার বর্তমান প্ল্যান অপরিবর্তিত রয়েছে।',
  'payment.viewPlans': 'প্ল্যান দেখুন',

  // ─── Admin ─────────────────────────────────────────────────────────────────
  'admin.searchUsers': 'নাম বা ইমেইল দিয়ে খুঁজুন',
  'admin.colUser': 'ব্যবহারকারী',
  'admin.colPlan': 'প্ল্যান',
  'admin.colSignedUp': 'নিবন্ধন',
  'admin.colLastSeen': 'সর্বশেষ সক্রিয়',
  'admin.colGenerations': 'জেনারেশন',
  'admin.roleBadge': 'প্রশাসক',
  'admin.viewActivity': 'কার্যক্রম দেখুন',
  'admin.colAccount': 'অ্যাকাউন্ট',
  'admin.accountOk': 'সক্রিয়',
  'admin.bannedBadge': 'স্থগিত',
  'admin.bannedUntil': '{date} পর্যন্ত',
  'admin.ban': 'স্থগিত করুন',
  'admin.unban': 'প্রত্যাহার',
  'admin.banTitle': 'এই অ্যাকাউন্ট স্থগিত করুন',
  'admin.banDescription':
    '{user} কে সাইন আউট করা হবে এবং API ও পুনরায় সাইন ইন করা থেকে বিরত রাখা হবে।',
  'admin.banDuration': 'মেয়াদ',
  'admin.banDays': '{days} দিন',
  'admin.banIndefinite': 'অনির্দিষ্টকাল',
  'admin.banReason': 'কারণ',
  'admin.banReasonPlaceholder': 'যেমন: জেনারেশন এন্ডপয়েন্টের স্বয়ংক্রিয় অপব্যবহার',
  'admin.banReasonHint': 'অ্যাপ ব্যবহারের প্রতিটি চেষ্টায় ব্যবহারকারীকে দেখানো হবে।',
  'admin.banConfirm': 'অ্যাকাউন্ট স্থগিত করুন',
  'admin.banFailed': 'অ্যাকাউন্টটি স্থগিত করা যায়নি।',
  'admin.unbanFailed': 'স্থগিতাদেশ প্রত্যাহার করা যায়নি।',
  'admin.noUsers': 'এই অনুসন্ধানে কোনো ব্যবহারকারী মেলেনি।',
  'admin.noActivity':
    'এখনো কিছু নথিভুক্ত হয়নি। মানুষ পণ্যটি ব্যবহার করলে কার্যক্রম এখানে দেখা যাবে।',
  'admin.filterAll': 'সব ধরনের ইভেন্ট',
  'admin.filterClear': 'ফিল্টার মুছুন',
  'admin.filteredToUser': 'শুধু একজন ব্যবহারকারীর কার্যক্রম দেখানো হচ্ছে।',
  'admin.showing': '{total} টির মধ্যে {from}–{to} দেখানো হচ্ছে',
  'admin.never': 'কখনো নয়',
  'admin.statTotalUsers': 'মোট ব্যবহারকারী',
  'admin.statNewUsers': '{days} দিনে নতুন',
  'admin.statActiveSubs': 'সক্রিয় সাবস্ক্রিপশন',
  'admin.statGenerations': 'তৈরি কনটেন্ট',
  'admin.statPostsPublished': 'প্রকাশিত পোস্ট',
  'admin.statConnectedAccounts': 'সংযুক্ত অ্যাকাউন্ট',
  'admin.statNeedsReauth': 'পুনঃসংযোগ প্রয়োজন',
  'admin.statRevenue': 'আয় ({days}দি)',
  'admin.perDayTitle': 'প্রতিদিনের জেনারেশন',
  'admin.postsPerDayTitle': 'প্রতিদিন প্রকাশিত পোস্ট',
  'admin.planDistTitle': 'সক্রিয় প্ল্যান',
  'admin.platformDistTitle': 'সংযুক্ত প্ল্যাটফর্ম',
  'admin.eventBreakdownTitle': 'ধরন অনুযায়ী ইভেন্ট',
  'admin.rangeDays': 'সর্বশেষ {days} দিন',
  'admin.noStats': 'এখনো যথেষ্ট তথ্য নেই।',

  // ─── Admin: activity event labels ──────────────────────────────────────────
  'activity.user_signed_up': 'নিবন্ধন করেছেন',
  'activity.user_signed_in': 'সাইন ইন করেছেন',
  'activity.content_generated': 'কনটেন্ট তৈরি করেছেন',
  'activity.generation_blocked': 'জেনারেশন বাধাগ্রস্ত',
  'activity.generation_failed': 'জেনারেশন ব্যর্থ',
  'activity.brand_created': 'ব্র্যান্ড তৈরি করেছেন',
  'activity.account_connected': 'অ্যাকাউন্ট সংযুক্ত করেছেন',
  'activity.account_disconnected': 'অ্যাকাউন্ট বিচ্ছিন্ন করেছেন',
  'activity.account_reauth_required': 'অ্যাকাউন্ট পুনঃসংযোগ প্রয়োজন',
  'activity.post_scheduled': 'পোস্ট নির্ধারণ করেছেন',
  'activity.post_published': 'পোস্ট প্রকাশিত',
  'activity.post_failed': 'পোস্ট প্রকাশ ব্যর্থ',
  'activity.post_approved': 'পোস্ট অনুমোদন করেছেন',
  'activity.post_rejected': 'পোস্ট প্রত্যাখ্যান করেছেন',
  'activity.token_refresh_failed': 'টোকেন রিফ্রেশ ব্যর্থ',
  'activity.payment_initiated': 'পেমেন্ট শুরু করেছেন',
  'activity.payment_completed': 'পেমেন্ট সম্পন্ন',
  'activity.payment_failed': 'পেমেন্ট ব্যর্থ',
  'activity.subscription_activated': 'প্ল্যান সক্রিয়',
  'activity.subscription_cancelled': 'প্ল্যান বাতিল',
  'activity.user_banned': 'অ্যাকাউন্ট স্থগিত',
  'activity.user_unbanned': 'স্থগিতাদেশ প্রত্যাহার',
  'activity.tenant_created': 'ওয়ার্কস্পেস তৈরি হয়েছে',
  'activity.tenant_deleted': 'ওয়ার্কস্পেস মুছে ফেলা হয়েছে',
  'activity.tenant_plan_changed': 'ওয়ার্কস্পেসের প্ল্যান পরিবর্তিত',
  'activity.tenant_admin_changed': 'ওয়ার্কস্পেস অ্যাডমিন পরিবর্তিত',
  'activity.member_invited': 'সদস্যকে আমন্ত্রণ জানানো হয়েছে',
  'activity.member_joined': 'ওয়ার্কস্পেসে যোগ দিয়েছেন',
  'activity.member_removed': 'সদস্যকে সরানো হয়েছে',
  'activity.member_role_changed': 'সদস্যের ভূমিকা পরিবর্তিত',
  'activity.permission_granted': 'অনুমতি দেওয়া হয়েছে',
  'activity.permission_revoked': 'অনুমতি প্রত্যাহার করা হয়েছে',
  'activity.team_created': 'টিম তৈরি হয়েছে',
  'activity.team_deleted': 'টিম মুছে ফেলা হয়েছে',
  'activity.team_membership_changed': 'টিমের সদস্যপদ পরিবর্তিত',
  'activity.team_visibility_changed': 'টিম দৃশ্যমানতা পরিবর্তিত',
  'activity.question_generated': 'প্রশ্নপত্র তৈরি করেছেন (পুরনো)',

  // ─── Media library ─────────────────────────────────────────────────────────
  'media.title': 'মিডিয়া লাইব্রেরি',
  'media.upload': 'আপলোড',
  'media.dropHint': 'এখানে ইমেজ বা ভিডিও ড্রপ করুন অথবা ব্রাউজ করতে ক্লিক করুন',
  'media.sizeHint': 'ইমেজ সর্বোচ্চ ১০০ MB · ভিডিও সর্বোচ্চ ১০ GB',
  'media.uploading': '{name} আপলোড হচ্ছে',
  'media.uploadError': 'আপলোড ব্যর্থ হয়েছে',
  'media.fileTooLarge': 'ফাইলের আকার সীমা অতিক্রম করেছে',
  'media.unsupportedType': 'ফাইলের ধরন সমর্থিত নয়',
  'media.filterAll': 'সব',
  'media.filterImages': 'ইমেজ',
  'media.filterVideos': 'ভিডিও',
  'media.filterActive': 'সক্রিয়',
  'media.filterArchived': 'আর্কাইভ',
  'media.filterAllStatus': 'সব স্ট্যাটাস',
  'media.searchPlaceholder': 'ফাইলের নাম খুঁজুন…',
  'media.empty': 'এখনো কোনো মিডিয়া নেই',
  'media.emptyHint': 'উপরের বোতাম ব্যবহার করে ইমেজ বা ভিডিও আপলোড করুন।',
  'media.emptyFilterHint': 'ফিল্টারের সাথে কোনো ফাইল মেলেনি।',
  'media.archive': 'আর্কাইভ',
  'media.restore': 'পুনরুদ্ধার',
  'media.delete': 'স্থায়ীভাবে মুছুন',
  'media.view': 'দেখুন',
  'media.archiveTitle': 'এই ফাইলটি আর্কাইভ করবেন?',
  'media.archiveBody': 'ফাইলটি আর্কাইভ ফোল্ডারে সরানো হবে। পরে পুনরুদ্ধার করা যাবে।',
  'media.deleteTitle': 'স্থায়ীভাবে মুছবেন?',
  'media.deleteBody': 'এটি পূর্বাবস্থায় ফেরানো যাবে না। ফাইলটি স্টোরেজ থেকে সরিয়ে দেওয়া হবে।',
  'media.adminBadge': 'সব ক্রিয়েটর',
  'media.ownerLabel': '{email} আপলোড করেছেন',
  'media.dimensions': '{w} × {h}',
  'media.duration': '{s} সেকেন্ড',
  'media.cancelUpload': 'বাতিল',
  'media.dismissError': 'বাতিল',

  // ─── Brands ────────────────────────────────────────────────────────────────
  'brands.setupTitle': 'আপনার প্রথম ব্র্যান্ড তৈরি করুন',
  'brands.setupSubtitle':
    'প্রতিটি পোস্ট এই প্রোফাইল থেকে লেখা হবে। যেকোনো সময় আরও বিবরণ যোগ করতে পারবেন।',
  'brands.nameLabel': 'ব্যবসার নাম',
  'brands.namePlaceholder': 'যেমন: সূর্যোদয় ক্যাফে',
  'brands.verticalLabel': 'শিল্প',
  'brands.timezoneLabel': 'টাইমজোন',
  'brands.timezonePlaceholder': 'যেমন: Asia/Dhaka',
  'brands.create': 'ব্র্যান্ড তৈরি করুন',
  'brands.creating': 'তৈরি হচ্ছে…',
  'brands.createError': 'ব্র্যান্ড তৈরি করা সম্ভব হয়নি। আবার চেষ্টা করুন।',
  'brands.limitReached': 'আপনার প্ল্যানে {count}টি ব্র্যান্ড অনুমোদিত। আরও যোগ করতে আপগ্রেড করুন।',
  'brands.listTitle': 'আপনার ব্র্যান্ডসমূহ',
  'brands.addBrand': 'ব্র্যান্ড যোগ করুন',

  // ─── Composer ──────────────────────────────────────────────────────────────
  'composer.pageTitle': 'নতুন পোস্ট তৈরি করুন',
  'composer.pageSubtitle': 'একাধিক সোশ্যাল মিডিয়া প্ল্যাটফর্মে প্রকাশ করুন',
  'composer.aiAssistant': 'AI সহকারী',
  'composer.aiComingSoon':
    'কনটেন্ট জেনারেশন API চালু হলে AI লেখার সহায়তা যুক্ত হবে। ততক্ষণ পোস্টটি নিজে লিখুন।',
  'composer.publishOptions': 'প্রকাশের অপশন',
  'composer.limitsOk': 'সব সীমা ঠিক আছে',
  'composer.limitsExceeded': '{count}টি সীমা ছাড়িয়েছে',

  'composer.tool.bold': 'বোল্ড',
  'composer.tool.italic': 'ইটালিক',
  'composer.tool.underline': 'আন্ডারলাইন',
  'composer.tool.bulletList': 'বুলেট তালিকা',
  'composer.tool.numberedList': 'সংখ্যাযুক্ত তালিকা',
  'composer.tool.mention': 'কাউকে মেনশন করুন',
  'composer.tool.link': 'লিংক যোগ করুন',
  'composer.tool.addImage': 'ছবি যোগ করুন',
  'composer.tool.addGif': 'GIF যোগ করুন',
  'composer.tool.addVideo': 'ভিডিও যোগ করুন',
  'composer.tool.addLocation': 'অবস্থান যোগ করুন',
  'composer.emoji': 'ইমোজি',
  'composer.hashtag': 'হ্যাশট্যাগ যোগ করুন',
  'composer.textPlaceholder': 'কী ভাবছেন? এখানে পোস্ট লিখুন…',
  'composer.saveDraft': 'ড্রাফট সংরক্ষণ',
  'composer.draftSaved': 'ড্রাফট সংরক্ষিত',
  'composer.noAccounts': 'কোনো অ্যাকাউন্ট সংযুক্ত নেই',
  'composer.noAccountsHint': 'প্রকাশ শুরু করতে সোশ্যাল অ্যাকাউন্ট সংযুক্ত করুন।',

  'composer.section.compose': 'পোস্ট লিখুন',
  'composer.section.schedule': 'কখন প্রকাশ করবেন',
  'composer.section.platformOptions': 'প্ল্যাটফর্ম সেটিংস',
  'composer.options.selectPlatformFirst': 'সেটিংস দেখতে উপরে অন্তত একটি প্ল্যাটফর্ম নির্বাচন করুন।',

  'composer.library.title': 'মিডিয়া লাইব্রেরি',
  'composer.library.search': 'মিডিয়া খুঁজুন…',
  'composer.library.noResults': 'এই অনুসন্ধানে কিছু পাওয়া যায়নি।',
  'composer.library.hint': 'পোস্টে যুক্ত করতে ফাইলে ক্লিক করুন।',
  'composer.library.add': 'পোস্টে যুক্ত করুন',
  'composer.library.remove': 'পোস্ট থেকে সরান',
  'composer.library.collapse': 'মিডিয়া লাইব্রেরি সংকুচিত করুন',
  'composer.library.expand': 'মিডিয়া লাইব্রেরি প্রসারিত করুন',
  'composer.section.preview': 'লাইভ প্রিভিউ',

  'composer.media.dropHint': 'এখানে ইমেজ বা ভিডিও ড্রপ করুন অথবা ব্রাউজ করতে ক্লিক করুন',
  'composer.media.types': 'JPEG, PNG, GIF, WebP, MP4, MOV · প্ল্যাটফর্ম সীমা প্রযোজ্য',
  'composer.media.upload': 'মিডিয়া আপলোড',
  'composer.media.addMore': 'আরও যোগ করুন',
  'composer.media.remove': '{name} সরান',
  'composer.media.itemLabel': '{name} — {total}টির মধ্যে {position} নম্বর',
  'composer.media.tooManyImages':
    'নির্বাচিত কোনো একটি প্ল্যাটফর্মে সর্বোচ্চ {max}টি ছবি দেওয়া যায়। সব জায়গায় প্রকাশ করতে কিছু সরান।',

  'composer.schedule.now': 'এখনই প্রকাশ',
  'composer.schedule.nowDesc': 'তাৎক্ষণিকভাবে প্রকাশ করুন',
  'composer.schedule.schedule': 'সময় নির্ধারণ',
  'composer.schedule.scheduleDesc': 'তারিখ ও সময় বেছে নিন',
  'composer.schedule.draft': 'ড্রাফট সংরক্ষণ',
  'composer.schedule.draftDesc': 'প্রকাশ না করে সংরক্ষণ',
  'composer.schedule.dateTime': 'তারিখ ও সময়',
  'composer.schedule.timezone': 'টাইমজোন',
  'composer.schedule.willPublishAt': 'প্রকাশিত হবে',
  'composer.schedule.noDateSet': 'তারিখ নির্ধারিত হয়নি',

  'composer.options.suffix': 'অপশন',
  'composer.options.noOptions': 'এই প্ল্যাটফর্মের জন্য অতিরিক্ত কোনো অপশন নেই।',
  'composer.options.fb.disableComments': 'মন্তব্য নিষ্ক্রিয় করুন',
  'composer.options.ig.postType': 'পোস্টের ধরন',
  'composer.options.ig.feed': 'ফিড',
  'composer.options.ig.reel': 'রিল',
  'composer.options.ig.story': 'স্টোরি',
  'composer.options.ig.firstComment': 'প্রথম মন্তব্য',
  'composer.options.ig.firstCommentPlaceholder': 'প্রথম মন্তব্যে হ্যাশট্যাগ যোগ করুন…',
  'composer.options.ig.location': 'অবস্থান',
  'composer.options.ig.locationPlaceholder': 'যেমন: ঢাকা, বাংলাদেশ',

  'composer.preview.empty': 'প্রিভিউ দেখতে একটি অ্যাকাউন্ট বেছে নিন',
  'composer.preview.emptyHint': 'প্ল্যাটফর্ম বেছে নিলে এখানে প্রিভিউ দেখাবে।',
  'composer.preview.justNow': 'এইমাত্র',
  'composer.preview.noText': 'আপনার পোস্টের লেখা এখানে দেখা যাবে।',
  'composer.preview.comments': '{count}টি মন্তব্য',
  'composer.preview.shares': '{count}টি শেয়ার',
  'composer.preview.like': 'লাইক',
  'composer.preview.comment': 'মন্তব্য',
  'composer.preview.share': 'শেয়ার',
  'composer.preview.reply': 'উত্তর',
  'composer.preview.repost': 'রিপোস্ট',
  'composer.preview.send': 'পাঠান',
  'composer.preview.save': 'সংরক্ষণ',

  'composer.summary.title': 'প্রকাশনার সারসংক্ষেপ',
  'composer.summary.platforms': 'প্ল্যাটফর্মসমূহ',
  'composer.summary.noPlatforms': 'কিছু নির্বাচিত হয়নি',
  'composer.summary.media': 'মিডিয়া',
  'composer.summary.files': '{count}টি ফাইল',
  'composer.summary.totalChars': 'মোট অক্ষর',
  'composer.summary.estimatedTime': 'আনুমানিক প্রকাশের সময়',
  'composer.summary.minutesRange': '{min} - {max} মিনিট',
  'composer.summary.status': 'অবস্থা',
  'composer.summary.statusReady': 'প্রকাশের জন্য প্রস্তুত',
  'composer.summary.statusScheduled': 'শিডিউলের জন্য প্রস্তুত',
  'composer.summary.statusDraft': 'ড্রাফট হিসেবে সংরক্ষিত হবে',
  'composer.summary.statusBlocked': 'সংশোধন প্রয়োজন',
  'composer.summary.content': 'কনটেন্ট',
  'composer.summary.chars': 'অক্ষর',
  'composer.summary.image': 'ছবি',
  'composer.summary.images': 'ছবি',
  'composer.summary.video': 'ভিডিও',
  'composer.summary.videos': 'ভিডিও',
  'composer.summary.schedule': 'সময়সূচি',
  'composer.summary.issues': 'সমস্যা',

  'composer.publish.now': 'এখনই প্রকাশ',
  'composer.publish.schedule': 'পোস্ট শিডিউল করুন',
  'composer.publish.draft': 'ড্রাফট সংরক্ষণ',

  'composer.publishing.inProgress': 'পোস্ট প্রকাশিত হচ্ছে…',
  'composer.publishing.allSuccess': 'সফলভাবে প্রকাশিত!',
  'composer.publishing.partialSuccess': 'আংশিকভাবে প্রকাশিত',
  'composer.publishing.publishedTo': '{count}টি প্ল্যাটফর্মে প্রকাশিত',
  'composer.publishing.nSuccess': '{count}টি সফল',
  'composer.publishing.nFailed': '{count}টি ব্যর্থ',
  'composer.publishing.retry': 'পুনরায় চেষ্টা',
  'composer.publishing.retryAll': 'ব্যর্থগুলো পুনরায় চেষ্টা',
  'composer.publishing.newPost': 'নতুন পোস্ট',
  'composer.publishing.doNotClose': 'প্রকাশনা চলছে — অনুগ্রহ করে এই উইন্ডো খোলা রাখুন',
  'composer.publishing.status.queued': 'অপেক্ষায়…',
  'composer.publishing.status.uploading': 'মিডিয়া আপলোড হচ্ছে…',
  'composer.publishing.status.publishing': 'প্রকাশ হচ্ছে…',
  'composer.publishing.status.success': 'প্রকাশিত ✓',
  'composer.publishing.status.failed': 'ব্যর্থ',
};
